import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  BookOpen,
  Calendar,
  ChevronDown,
  ChevronRight,
  Clipboard,
  ClipboardCheck,
  Download,
  ExternalLink,
  FileText,
  MessageSquare,
  TriangleAlert
} from "lucide-react";
import type { AdvisoryTljDraft } from "@/lib/cosmos-api";
import { Button, Card, StatusBadge } from "@/components/ui-ext/primitives";
import {
  downloadTextFile,
  draftFileStem,
  draftIngestionJson,
  draftMasterDocCsv
} from "./advisory-export";

const formatMinutes = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const rest = Math.round(minutes % 60);
  if (hours === 0) return `${rest}m`;
  return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
};

const elementTone = (
  elementType: string
): "ai" | "info" | "azure" | "neutral" => {
  if (elementType === "CertificateProgram") return "ai";
  if (elementType === "Course") return "info";
  if (elementType === "Module") return "azure";
  return "neutral";
};

export function TljDraftView({ draft }: { draft: AdvisoryTljDraft }) {
  const [showJson, setShowJson] = useState(false);
  const [copied, setCopied] = useState(false);

  const phases = useMemo(() => {
    const groups = new Map<string, AdvisoryTljDraft["structureItems"]>();
    for (const item of draft.structureItems) {
      const phase = item.phase ?? "Additional recommendations";
      const bucket = groups.get(phase) ?? [];
      bucket.push(item);
      groups.set(phase, bucket);
    }
    return [...groups.entries()];
  }, [draft.structureItems]);

  const budgetPercent = draft.timeBudget.requestedHours
    ? Math.min(
        150,
        Math.round(
          (draft.timeBudget.selectedMinutes /
            (draft.timeBudget.requestedHours * 60)) *
            100
        )
      )
    : null;

  const copyJson = async () => {
    await navigator.clipboard.writeText(draftIngestionJson(draft));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div className="space-y-4">
      <Card accent className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-[var(--primary)]">
              {draft.createJourneyCommand.Type === "CertificateProgram"
                ? "Certificate program draft"
                : "Course draft"}
            </div>
            <h2 className="mt-0.5 text-lg font-bold text-anchor">{draft.title}</h2>
            <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
              {draft.createJourneyCommand.Description}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span className="font-mono">{draft.createJourneyCommand.SKU}</span>
              <span>·</span>
              <span>Difficulty: {draft.createJourneyCommand.DifficultyLevel}</span>
              <span>·</span>
              <span>Private, standalone</span>
              {draft.recommendationEngine && (
                <>
                  <span>·</span>
                  <span>engine: {draft.recommendationEngine}</span>
                </>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" size="sm" onClick={copyJson}>
              {copied ? (
                <ClipboardCheck className="h-3.5 w-3.5" />
              ) : (
                <Clipboard className="h-3.5 w-3.5" />
              )}
              {copied ? "Copied" : "Copy LMS JSON"}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                downloadTextFile(
                  `${draftFileStem(draft)}.json`,
                  draftIngestionJson(draft),
                  "application/json"
                )
              }
            >
              <Download className="h-3.5 w-3.5" />
              JSON
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                downloadTextFile(
                  `${draftFileStem(draft)}.csv`,
                  draftMasterDocCsv(draft),
                  "text/csv"
                )
              }
            >
              <FileText className="h-3.5 w-3.5" />
              Master-Doc CSV
            </Button>
            <Link
              to="/workspace"
              search={{ prompt: `/recommend ${draft.prompt}` }}
            >
              <Button variant="ghost" size="sm">
                <MessageSquare className="h-3.5 w-3.5" />
                Refine in Workspace
              </Button>
            </Link>
          </div>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <div className="rounded-[10px] border border-soft bg-muted/40 p-3">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Time budget
            </div>
            <div className="mt-1 text-sm font-semibold text-anchor">
              {formatMinutes(draft.timeBudget.selectedMinutes)}
              {draft.timeBudget.requestedHours
                ? ` / ${draft.timeBudget.requestedHours}h requested`
                : " selected"}
            </div>
            {budgetPercent !== null && (
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
                <div
                  className={
                    budgetPercent > 120
                      ? "h-full bg-[var(--danger)]"
                      : "h-full bg-[var(--primary)]"
                  }
                  style={{ width: `${Math.min(budgetPercent, 100)}%` }}
                />
              </div>
            )}
            <div className="mt-1.5 text-[11px] text-muted-foreground">
              {draft.timeBudget.note}
            </div>
          </div>
          <div className="rounded-[10px] border border-soft bg-muted/40 p-3">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Competency coverage
            </div>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {draft.competencyCoverage.length > 0 ? (
                draft.competencyCoverage.map((competency) => (
                  <StatusBadge key={competency} tone="ai">
                    {competency}
                  </StatusBadge>
                ))
              ) : (
                <span className="text-xs text-muted-foreground">
                  No T-shape competencies detected.
                </span>
              )}
            </div>
          </div>
          <div className="rounded-[10px] border border-soft bg-muted/40 p-3">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Structure
            </div>
            <div className="mt-1 text-sm font-semibold text-anchor">
              {draft.structureItems.length} items ·{" "}
              {draft.supportingResources.length} optional resources
            </div>
            <div className="mt-1.5 text-[11px] text-muted-foreground">
              References real catalog ids only — nothing invented.
            </div>
          </div>
        </div>
      </Card>

      {phases.map(([phase, items]) => (
        <Card key={phase} className="p-4">
          <div className="mb-2 flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-[var(--primary)]" />
            <h3 className="text-sm font-semibold text-anchor">{phase}</h3>
          </div>
          <ol className="space-y-2">
            {items.map((item) => (
              <li
                key={`${item.elementType}-${item.elementId}`}
                className="rounded-[10px] border border-soft p-3"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge tone={elementTone(item.elementType)}>
                    {item.elementType}
                  </StatusBadge>
                  <span className="text-sm font-medium text-anchor">
                    {item.url ? (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 hover:text-[var(--primary)]"
                      >
                        {item.title}
                        <ExternalLink className="h-3 w-3 opacity-60" />
                      </a>
                    ) : (
                      item.title
                    )}
                  </span>
                  <span className="ml-auto flex items-center gap-2 text-[11px] text-muted-foreground">
                    {item.sku && <span className="font-mono">{item.sku}</span>}
                    <span className="font-mono">#{item.elementId}</span>
                    {item.durationMinutes !== undefined && (
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatMinutes(item.durationMinutes)}
                      </span>
                    )}
                  </span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{item.why}</p>
              </li>
            ))}
          </ol>
        </Card>
      ))}

      {draft.supportingResources.length > 0 && (
        <Card className="p-4">
          <h3 className="mb-2 text-sm font-semibold text-anchor">
            Optional resources & parallel proposals
          </h3>
          <ul className="space-y-1.5">
            {draft.supportingResources.map((resource) => (
              <li key={`${resource.entityType}-${resource.id}`} className="text-sm">
                <span className="font-medium text-anchor">
                  {resource.url ? (
                    <a
                      href={resource.url}
                      target="_blank"
                      rel="noreferrer"
                      className="hover:text-[var(--primary)]"
                    >
                      {resource.title}
                    </a>
                  ) : (
                    resource.title
                  )}
                </span>
                <span className="text-xs text-muted-foreground"> — {resource.why}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card className="border-[var(--warning)]/40 bg-[var(--warning)]/5 p-4">
        <div className="mb-2 flex items-center gap-2">
          <TriangleAlert className="h-4 w-4 text-[#9a6500]" />
          <h3 className="text-sm font-semibold text-anchor">
            Before this can be built in the LMS
          </h3>
        </div>
        <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground">
          {draft.todos.map((todo) => (
            <li key={todo}>{todo}</li>
          ))}
        </ul>
      </Card>

      <Card className="p-4">
        <button
          type="button"
          className="flex w-full items-center gap-2 text-left text-sm font-semibold text-anchor"
          onClick={() => setShowJson((value) => !value)}
        >
          {showJson ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
          LMS ingestion payload (CreateJourneyCommand)
        </button>
        {showJson && (
          <pre className="mt-3 max-h-96 overflow-auto rounded-[10px] bg-[var(--sidebar)] p-3 text-[11px] leading-relaxed text-white/90">
            {draftIngestionJson(draft)}
          </pre>
        )}
      </Card>
    </div>
  );
}
